import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AlarmGroupDialogComponent } from '@components/alarm-group-setting/alarm-group-dialog/alarm-group-dialog.component';
import { Vessel } from 'icreate/common/models/vessel';
import { firstValueFrom, lastValueFrom, Subject, takeUntil } from 'rxjs';
import _ from 'lodash';
import { MessageDialogService } from '@service/message-dialog.service';
import { AlarmGroup } from 'icreate/common/models/alarm-group';
import { AlarmGroupSocketService } from '@service/alarm-group-socket.service';
import { AlarmGroupService } from '@service/alarm-group.service';
import { AlarmGroupHelperService } from '@service/alarm-group-helper.service';
import { PrintAlarmGroupService } from '@service/channel/print-alarm-groups/print-alarm-groups.service';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationWindowTypes } from '@components/confirmation-window/confirmation-window.component';

@Component({
    selector: 'app-alarm-group-setting',
    templateUrl: './alarm-group-setting.component.html',
    styleUrl: './alarm-group-setting.component.css',
    standalone: false,
})
export class AlarmGroupSettingComponent {
    @Input() vessel!: Vessel;
    @Input() userCanEdit: boolean = true;
    @Output() pageUpdated = new EventEmitter<boolean>();

    alarmGroupList: AlarmGroup[];
    initialAlarmGroupList: AlarmGroup[];

    alarmGroupPageEditMode: boolean = false;
    isLoading: boolean = true;
    maxAlarmGroups: number = 24;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private alarmGroupSocketService: AlarmGroupSocketService,
        private alarmGroupService: AlarmGroupService,
        private alarmGroupHelperService: AlarmGroupHelperService,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private messageDialogService: MessageDialogService,
        private alarmGroupPrintService: PrintAlarmGroupService
    ) {}

    @HostListener('window:beforeunload', ['$event'])
    async beforeUnload(event) {
        if (this.hasUnsavedChanges()) {
            event.preventDefault();
        }
    }

    async ngOnInit() {
        this.alarmGroupList = (await this.alarmGroupService.getAll(this.vessel.id)) as any[];
        this.initialAlarmGroupList = _.cloneDeep(this.alarmGroupList);

        this.alarmGroupSocketService
            .alarmGroupsUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(({ alarmGroups, vesselId }) => {
                if (vesselId === this.vessel.id) {
                    this.alarmGroupList = alarmGroups;
                    this.showMessageDialog('alarm-group.update.message', '');
                }
            });
        this.alarmGroupHelperService.loadingEmitter
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(loading => {
                this.isLoading = loading;
                this.cdr.detectChanges();
            });

        this.alarmGroupHelperService.pageUpdateEmitter
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(() => {
                this.onGroupUpdated();
                this.cdr.detectChanges();
            });

        this.isLoading = false;
    }

    async openCreateDialog() {
        if (!this.userCanEdit) return;
        const dialogRef = this.dialog.open(AlarmGroupDialogComponent, {
            data: {
                name: '',
                description: '',
                vesselId: this.vessel.id,
                groupNo: this.alarmGroupList.length,
                alarmGroupList: this.alarmGroupList,
            },
            disableClose: true,
        });

        const result = await firstValueFrom(dialogRef.afterClosed());
        if (result) {
            this.alarmGroupList.push(result.alarmGroup);
            this.onGroupUpdated();
        }
    }

    showMessageDialog(message, name) {
        this.messageDialogService.openMessage(message, { name: name });
    }

    async alarmGroupDrop(event) {
        try {
            this.alarmGroupHelperService.showLoading();
            await this.alarmGroupService.moveItem(event.previousIndex, event.currentIndex, this.vessel.id);
        } catch (error) {
            console.log(error);
        } finally {
            this.alarmGroupHelperService.hideLoading();
        }
    }

    hasUnsavedChanges() {
        return this.userCanEdit && !_.isEqual(this.initialAlarmGroupList, this.alarmGroupList);
    }
    async printGroups() {
        if (this.hasUnsavedChanges()) {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '474px',
                data: {
                    type: ConfirmationDialogType.COMPLEX,
                    title: 'confirmation-dialog.print.title',
                    message: 'confirmation-dialog.print.message',
                    backButton: 'confirmation-dialog.print.back',
                    discardButton: 'confirmation-dialog.print.discard',
                    saveButton: 'confirmation-dialog.print.save',
                },
            });
            const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

            if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
                return;
            } else if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                await this.save();
            }
        }

        try {
            await this.alarmGroupPrintService.triggerPrint(this.vessel, true);
        } catch (err) {
            console.error('Error in printSensorCableTable:', err);
            throw err;
        }
    }

    private generateDuplicateName(originalName: string, existingNames: string[]): string {
        const base = originalName || '';
        const match = base.match(/_copy(?:_(\d+))?$/);
        const trueBaseName = match ? base.substring(0, match.index) : base;
        const usedNumbers = new Set<number>();

        for (const name of existingNames) {
            if (!name.startsWith(trueBaseName)) continue;

            const suffixMatch = name.substring(trueBaseName.length).match(/^_copy(?:_(\d+))?$/);

            if (suffixMatch) {
                const num = suffixMatch[1] ? parseInt(suffixMatch[1], 10) : 1;
                usedNumbers.add(num);
            }
        }
        let nextNum = 1;
        while (usedNumbers.has(nextNum)) {
            nextNum++;
        }
        return nextNum === 1 ? `${trueBaseName}_copy` : `${trueBaseName}_copy_${nextNum}`;
    }

    async duplicateRow(index: number) {
        if (!this.userCanEdit) return;
        if (this.alarmGroupList.length >= this.maxAlarmGroups) return;

        const src = this.alarmGroupList[index];
        const existingNames = this.alarmGroupList.map(group => group.name || '');

        const dialogRef = this.dialog.open(AlarmGroupDialogComponent, {
            data: {
                name: this.generateDuplicateName(src.name || '', existingNames),
                alarmGroupList: this.alarmGroupList,
                id: undefined,
                vesselId: this.vessel.id,
                groupNo: this.alarmGroupList.length,
                categories: [...(src.categories ?? [])],
                description: src.description,
                existingNames: existingNames,
            },
            disableClose: true,
        });

        const result = await firstValueFrom(dialogRef.afterClosed());
        if (result) {
            this.alarmGroupList.push(result.alarmGroup);
            this.onGroupUpdated();
        }
    }

    async save() {
        if (!this.userCanEdit) return;
        this.isLoading = true;
        try {
            await this.alarmGroupService.saveAll(this.alarmGroupList, this.vessel.id);
            this.initialAlarmGroupList = _.cloneDeep(this.alarmGroupList);
            this.onGroupUpdated();
        } catch (error) {
            console.log(error);
            throw new Error(error);
        } finally {
            this.isLoading = false;
        }
    }

    onGroupUpdated() {
        !_.isEqual(this.initialAlarmGroupList, this.alarmGroupList)
            ? this.pageUpdated.emit(true)
            : this.pageUpdated.emit(false);
    }

    async deleteRow(index) {
        if (!this.userCanEdit) return;
        this.alarmGroupList.splice(index, 1);
        this.onGroupUpdated();
    }
}
